import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SafetyGuidePage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-[72px]">
        {/* Article Hero */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[900px] mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/hipobuy-spreadsheet-guide" className="hover:text-primary-600 transition-colors cursor-pointer">Guide</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">Safety Guide</span>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 border border-accent-200/60 mb-6">
              <i className="ri-shield-check-line text-sm text-accent-600"></i>
              <span className="text-xs text-accent-700 font-medium">Safety & Security</span>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-6">
              Hipobuy Spreadsheet Safety Guide: How to Shop Securely in 2026
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-[700px] mb-8">
              A comprehensive safety resource covering everything from verifying seller credibility and
              spotting counterfeit listings to protecting your payment information and understanding
              third-party platform risks when using spreadsheet-based shopping tools.
            </p>

            <div className="flex flex-wrap items-center gap-6 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm"></i>
                Updated July 2026
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm"></i>
                14 min read
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line text-sm"></i>
                Safety Research Team
              </span>
            </div>
          </div>
        </section>

        {/* Banner Image */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Security%20and%20trust%20concept%20with%20a%20padlock%20icon%20next%20to%20a%20modern%20laptop%20on%20a%20light%20sand%20colored%20desk%2C%20soft%20natural%20window%20lighting%2C%20clean%20editorial%20photography%2C%20warm%20neutral%20tones%2C%20professional%20atmosphere%2C%20shield%20symbol%20subtle%20in%20composition%2C%20organized%20workspace%20with%20documents%2C%20high%20detail%2C%20trustworthy%20aesthetic%2C%20calm%20and%20reassuring%20mood&width=1800&height=800&seq=safety-hero-banner-v2&orientation=landscape"
            alt="Hipobuy Spreadsheet safety and security guide"
            title="Hipobuy Spreadsheet - Safety Guide"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/60 via-transparent to-background-50/60"></div>
        </section>

        {/* Article Content */}
        <article className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[800px] mx-auto px-6 md:px-10">
            <div className="prose-custom space-y-12">
              {/* Section 1: Why Safety Matters */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Why Spreadsheet Shopping Safety Matters
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  Safety should always be your top priority when using any third-party shopping resource, including Hipobuy Spreadsheet. While the spreadsheet itself is a community-maintained research tool with strong verification processes, the links within it direct you to external platforms where individual sellers operate independently. Understanding the safety landscape helps you shop with confidence and avoid common pitfalls.
                </p>
                <p className="text-foreground-600 leading-relaxed text-base mt-4">
                  We created this guide because we want every Hipobuy Spreadsheet user to have a positive and secure experience. The tips and checklists below draw from real experiences shared by the community, combined with best practices for online shopping safety. Whether you are making your first purchase or your hundredth, these principles remain essential.
                </p>
                <p className="text-foreground-600 leading-relaxed text-base mt-4">
                  For a broader overview of how the spreadsheet system works, check our{' '}
                  <Link to="/hipobuy-spreadsheet-guide" className="text-primary-600 underline underline-offset-2 hover:text-primary-700 transition-colors cursor-pointer">
                    complete guide
                  </Link>
                  . And if you want to see how Hipobuy stacks up against alternatives from a trust perspective, our{' '}
                  <Link to="/hipobuy-spreadsheet-review" className="text-primary-600 underline underline-offset-2 hover:text-primary-700 transition-colors cursor-pointer">
                    independent review
                  </Link>{' '}
                  covers credibility in depth.
                </p>
              </section>

              {/* Section 2: Common Risks */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Common Risks and How to Avoid Them
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base mb-6">
                  Awareness is your first line of defense. Here are the most common risks encountered when shopping through spreadsheet-linked platforms, along with practical countermeasures.
                </p>

                <div className="space-y-5">
                  {[
                    {
                      risk: 'Counterfeit or Misrepresented Products',
                      desc: 'Some sellers may list products that differ from the images or descriptions shown in the spreadsheet. Items may use lower-quality materials than advertised or feature incorrect branding.',
                      solutions: [
                        'Compare spreadsheet data against the actual product page details',
                        'Look for QC-verified badges in spreadsheet entries',
                        'Read the most recent reviews, not just the top-rated ones',
                        'Check seller history and overall rating before purchasing',
                      ],
                    },
                    {
                      risk: 'Broken or Outdated Links',
                      desc: 'Since spreadsheet links point to external platforms, sellers may remove or change product pages, leading to dead links or redirects to unrelated items.',
                      solutions: [
                        'Report broken links to the spreadsheet community',
                        'Use the search function on the destination platform as a fallback',
                        'Favor recently updated spreadsheet entries over older ones',
                      ],
                    },
                    {
                      risk: 'Payment Security Concerns',
                      desc: 'When redirected to third-party platforms, your payment information is handled by that platform, not by the spreadsheet resource. Different platforms have different security standards.',
                      solutions: [
                        'Verify the destination platform uses HTTPS and secure checkout',
                        'Use payment methods with buyer protection whenever possible',
                        'Never share payment details through direct messages or chat',
                        'Keep records of all transactions including order confirmations',
                      ],
                    },
                    {
                      risk: 'Phishing and Impersonation',
                      desc: 'Malicious actors may create fake spreadsheet copies or impersonation websites designed to collect user information or direct traffic to scam listings.',
                      solutions: [
                        'Only use spreadsheet links from trusted community sources',
                        'Verify the domain name of the destination before clicking',
                        'Bookmark the official main platform URL for direct access',
                        'Report suspicious copies to the community immediately',
                      ],
                    },
                  ].map((item) => (
                    <div key={item.risk} className="bg-background-100 rounded-2xl p-6">
                      <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                        <i className="ri-error-warning-line text-accent-600 text-xl"></i>
                        {item.risk}
                      </h3>
                      <p className="text-foreground-600 text-sm leading-relaxed mb-4">{item.desc}</p>
                      <div className="bg-background-50 rounded-xl p-4 border border-background-200/50">
                        <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">How to Protect Yourself</p>
                        <ul className="space-y-1.5">
                          {item.solutions.map((s) => (
                            <li key={s} className="flex items-start gap-2 text-sm text-foreground-700">
                              <i className="ri-check-line text-accent-600 text-sm mt-0.5 flex-shrink-0"></i>
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 3: Safety Checklist */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  The Ultimate Pre-Purchase Safety Checklist
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  Before clicking any purchase button through a spreadsheet link, run through this checklist. We recommend treating every item on this list as mandatory — skipping even one step increases your risk exposure unnecessarily.
                </p>
                <div className="mt-6 bg-background-100 rounded-2xl p-6 md:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { num: '01', text: 'Verify the seller has a rating of 4.5 stars or higher with at least 50 reviews' },
                      { num: '02', text: 'Confirm the product price on the destination page matches what the spreadsheet shows' },
                      { num: '03', text: 'Check that the product images on the destination page match the spreadsheet description' },
                      { num: '04', text: 'Read the 5 most recent reviews for any red flags about quality or shipping' },
                      { num: '05', text: 'Review the size chart carefully and compare against your own measurements' },
                      { num: '06', text: 'Confirm the material composition listed matches what you expect from the price point' },
                      { num: '07', text: 'Check the estimated shipping time and return policy before ordering' },
                      { num: '08', text: 'Ensure you are on the official platform domain, not a lookalike website' },
                      { num: '09', text: 'Take a screenshot of the product page for your records before purchasing' },
                      { num: '10', text: 'Use a payment method with buyer protection, never direct bank transfers' },
                    ].map((item) => (
                      <div key={item.num} className="flex items-start gap-3 bg-background-50 rounded-xl p-4 border border-background-200/40">
                        <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 font-bold text-xs">{item.num}</span>
                        <span className="text-sm text-foreground-700 leading-relaxed">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Section 4: QC Process */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Understanding the QC Verification Process
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  The Quality Control (QC) system is what sets Hipobuy Spreadsheet apart from random link collections. Every product entry goes through a multi-stage verification process before it appears in the spreadsheet. Understanding how this process works helps you interpret QC labels and make better-informed decisions.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { stage: 'Stage 1', title: 'Initial Submission', desc: 'Community members submit product links with basic information including name, category, and initial impressions. Each submission is timestamped and tracked.', color: 'bg-background-100' },
                    { stage: 'Stage 2', title: 'Peer Verification', desc: 'At least two other community members independently verify the product details, check seller credibility, and confirm the link accuracy before approving.', color: 'bg-accent-50 border border-accent-200/40' },
                    { stage: 'Stage 3', title: 'Ongoing Monitoring', desc: 'Approved entries are periodically rechecked. If seller ratings drop, products change, or links break, the entry is flagged for review or removal.', color: 'bg-background-100' },
                  ].map((item) => (
                    <div key={item.stage} className={`${item.color} rounded-2xl p-5`}>
                      <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">{item.stage}</span>
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mt-2 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 5: Payment Safety */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Payment Safety: Protecting Your Financial Information
                </h2>
                <p className="text-foreground-600 leading-relaxed text-base">
                  Payment security deserves special attention because it involves your most sensitive information. The spreadsheet itself never collects payment data — all transactions happen on the destination platform. This separation is actually a security advantage, as it means your financial details are handled by established platforms with dedicated security infrastructure.
                </p>
                <p className="text-foreground-600 leading-relaxed text-base mt-4">
                  <strong>Always use payment methods that include buyer protection.</strong> Credit cards, PayPal, and similar services give you recourse if something goes wrong. Never agree to pay through direct bank transfers, wire services, or cryptocurrency unless you fully understand and accept the irreversible nature of those payment methods. If a seller insists on an unconventional payment method, that is a significant red flag regardless of what the spreadsheet entry says.
                </p>

                <div className="mt-6 bg-accent-50 rounded-2xl p-6 border border-accent-200/50">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-bank-card-line text-accent-600 text-xl"></i>
                    Payment Safety Quick Tips
                  </h3>
                  <ul className="space-y-2">
                    {[
                      'Check that the checkout page URL starts with https:// (the "s" means secure)',
                      'Look for trust seals and security badges on the checkout page',
                      'Avoid saving payment information on shared or public devices',
                      'Monitor your bank statements regularly after making purchases',
                      'Set up transaction alerts on your payment methods for real-time monitoring',
                    ].map((tip) => (
                      <li key={tip} className="flex items-start gap-2.5 text-sm text-foreground-700">
                        <i className="ri-arrow-right-s-line text-accent-600 mt-0.5 flex-shrink-0"></i>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* FAQ */}
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  Safety Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {[
                    {
                      q: 'Is it safe to use Hipobuy Spreadsheet links?',
                      a: 'Yes, when you follow recommended safety practices. The spreadsheet links are community-verified and regularly checked for accuracy. However, always verify the destination page, check seller ratings, and use secure payment methods. The spreadsheet is a research tool — your safety depends on following the guidelines outlined in this guide.',
                    },
                    {
                      q: 'What should I do if I find a suspicious listing?',
                      a: 'Report it to the spreadsheet community immediately through the main platform. Include details about why the listing seems suspicious — mismatched pricing, broken links, counterfeit concerns, or unusual seller behavior. The community relies on user reports to maintain data quality and remove problematic entries quickly.',
                    },
                    {
                      q: 'Does the spreadsheet store my personal or payment information?',
                      a: 'No. Hipobuy Spreadsheet is purely a research resource and does not collect, store, or process any personal or payment information. All transactions occur on the destination platform. Never enter payment details on any page that claims to be part of the spreadsheet itself.',
                    },
                    {
                      q: 'How can I tell if a seller is trustworthy?',
                      a: 'Look for sellers with ratings above 4.5 stars, at least 50 completed transactions, detailed product descriptions with real photos (not just stock images), and recent positive reviews that mention specific product details. A seller with a long, consistent history is generally more reliable than a new seller with few reviews, even if the newer seller has perfect ratings.',
                    },
                  ].map((faq, i) => (
                    <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                      <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-200/50 transition-colors duration-150">
                        <h4 className="font-heading font-semibold text-foreground-950 text-base pr-4">{faq.q}</h4>
                        <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-open:rotate-45 transition-transform duration-200">
                          <i className="ri-add-line text-sm"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-5">
                        <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* CTA */}
              <section className="bg-primary-50 rounded-2xl p-8 md:p-10 text-center border border-primary-200/50">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  Shop with Confidence
                </h2>
                <p className="text-foreground-600 text-sm max-w-lg mx-auto mb-6">
                  Apply these safety practices and explore verified product categories on the main platform. Knowledge is your strongest protection.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-7 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors duration-200"
                  >
                    Visit Main Platform
                    <i className="ri-arrow-right-up-line ml-1.5"></i>
                  </a>
                  <Link
                    to="/hipobuy-spreadsheet-beginner-guide"
                    className="px-7 py-3 rounded-full border-2 border-primary-500 text-primary-700 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-50 transition-colors duration-200"
                  >
                    Beginner Guide
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      {/* BlogPosting Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            '@id': 'https://hipobuyspread.click/spreadsheet-safety-guide/#article',
            'headline': 'Hipobuy Spreadsheet Safety Guide: How to Shop Securely in 2026',
            'description': 'A comprehensive safety resource covering seller credibility verification, counterfeit spotting, payment protection, and third-party platform risks when using spreadsheet-based shopping tools.',
            'author': {
              '@type': 'Organization',
              'name': 'Hipobuy Spread',
              'url': 'https://hipobuyspread.click',
            },
            'publisher': {
              '@id': 'https://hipobuyspread.click/#organization',
            },
            'datePublished': '2026-07-01',
            'dateModified': '2026-07-12',
            'mainEntityOfPage': {
              '@type': 'WebPage',
              '@id': 'https://hipobuyspread.click/spreadsheet-safety-guide',
            },
            'isPartOf': {
              '@id': 'https://hipobuyspread.click/#website',
            },
          }),
        }}
      />

      {/* FAQ Schema structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            '@id': 'https://hipobuyspread.click/spreadsheet-safety-guide/#faq',
            'mainEntity': [
              {
                '@type': 'Question',
                'name': 'Is it safe to use Hipobuy Spreadsheet links?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': 'Yes, when following recommended safety practices. Always verify the destination page, check seller ratings, and use secure payment methods.',
                },
              },
              {
                '@type': 'Question',
                'name': 'Does the spreadsheet store my personal or payment information?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': 'No. Hipobuy Spreadsheet does not collect or store any personal or payment information. All transactions occur on the destination platform.',
                },
              },
              {
                '@type': 'Question',
                'name': 'How can I tell if a seller is trustworthy?',
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': 'Look for sellers with ratings above 4.5 stars, at least 50 completed transactions, detailed descriptions with real photos, and recent positive reviews.',
                },
              },
            ],
          }),
        }}
      />
    </div>
  );
}